import React, { useState, useEffect } from 'react';

import { HAS_ENVELOPE, COMPAIGNS_ACTION_TYPE } from '~/utils/constants';
import { AccessTime as AccessTimeIcon } from '@mui/icons-material';

import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Tooltip,
  IconButton,
  Fab,
  CircularProgress,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  MailOutlined as MailIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import {
  Delete2CIcon,
  DeleteWebhookCIcon,
  DownloadCIcon,
  EditCampaignCIconBold,
  EditCampaignStepCIcon,
  PlusStep,
} from '~/assets/images';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import ActionsModal from './ActionsModal';

const StepCard = ({
  step,
  index,
  steps,
  handleEditStep,
  handleDeleteStep,
  handleDownloadMailerProof,
  downloadingProof,
  handleDownloadEnvelopeProof,
  downloadingEnvelope,
  setEditingStep,
  setInsertIndex,
  setOpenDelayDialog,
  setOpenActionDialog,
}) => {
  const isLastStep = index === steps.length - 1;
  const isFirstStep = index === 0;
  const nextStepShouldBe = step.type === 'action' ? 'delay' : 'action';
  const [openActionModal, setOpenActionModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [wasDownloading, setWasDownloading] = useState({ proof: false, envelope: false });
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Watch for download completion and close menu
  useEffect(() => {
    const isProofDownloading = downloadingProof.status && downloadingProof.stepId === step.id;
    const isEnvelopeDownloading =
      downloadingEnvelope.status && downloadingEnvelope.stepId === step.id;

    if (wasDownloading.proof && !isProofDownloading) {
      handleMenuClose();
    }

    if (wasDownloading.envelope && !isEnvelopeDownloading) {
      handleMenuClose();
    }

    setWasDownloading({
      proof: isProofDownloading,
      envelope: isEnvelopeDownloading,
    });
  }, [
    downloadingProof,
    downloadingEnvelope,
    step.id,
    wasDownloading.proof,
    wasDownloading.envelope,
  ]);

  // Check if this delay is between two actions (should hide delete button)
  const isDelayBetweenActions =
    step.type === 'delay' &&
    steps[index - 1]?.type === 'action' &&
    steps[index + 1]?.type === 'action';

  return (
    <Box
      sx={{
        mb: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
      }}
    >
      {/* Connecting line from previous step */}
      {!isFirstStep && (
        <Box
          sx={{
            width: 2,
            height: '32px',
            backgroundColor: 'rgba(68, 66, 65, 0.3)',
            margin: '0 auto',
            marginBottom: '8px',
          }}
        />
      )}

      {/* Edit Button for current step (appears above step, except for first step) */}
      {!isFirstStep && (
        <>
          {/* Edit Button positioned closer to the step it edits */}
          <Tooltip title={`Edit ${step.label}`}>
            <Fab
              size="small"
              sx={{
                backgroundColor: 'white',
                border: '0.5px solid rgba(0, 0, 0, 0.16)',
                color: '#303030',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                mb: 1,
                '&:hover': {
                  backgroundColor: '#fff',
                  borderColor: '#ed5c2f',
                  color: '#ed5c2f',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
                width: 42,
                height: 42,
                minHeight: 42,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleEditStep(step);
              }}
            >
              <img
                src={EditCampaignCIconBold}
                style={{ width: '18px', height: '18px' }}
                alt="icon"
              />
            </Fab>
          </Tooltip>

          {/* Short line segment after edit button */}
          <Box
            sx={{
              width: 2,
              height: '32px',
              backgroundColor: 'rgba(68, 66, 65, 0.3)',
              margin: '0 auto',
              marginTop: '8px',
            }}
          />
        </>
      )}

      <Card
        sx={{
          borderRadius: '8px',
          border: '1px solid rgba(0, 0, 0, 0.16)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          width: '100%',
          maxWidth: step.type === 'delay' ? 300 : 500,
          minWidth: step.type === 'delay' ? '248px' : '366px',
          minHeight: step.type === 'delay' ? '80px' : '92px',
          transform: 'translateY(0)',
          cursor: 'pointer',
          position: 'relative',
          my: 1,
          boxShadow: '2px 2px 4px 0 rgba(0, 0, 0, 0.04)',
          '&:hover': {
            borderColor: '#ED5C2F',
            boxShadow: '2px 2px 4px 0 rgba(0, 0, 0, 0.04)',
            // transform: 'translateY(-2px)',
          },
        }}
        onClick={() => handleEditStep(step)}
      >
        {/* Actions Menu - Only for Actions */}
        {step.type === 'action' && (
          <Box
            sx={{
              position: 'absolute',
              top: '15px',
              right: '16px',
              display: 'flex',
              zIndex: 1,
            }}
          >
            <IconButton
              size="small"
              onClick={handleMenuClick}
              disableRipple
              sx={{
                opacity: anchorEl ? 1 : 0.4,
                width: 40,
                height: 40,
                borderRadius: 1,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'transparent',
                  opacity: 1,
                },
              }}
            >
              <MoreVertIcon fontSize="medium" />
            </IconButton>
          </Box>
        )}

        <CardContent
          className={step.type === 'delay' ? 'delayedCardWrapper' : 'actionCardWrapper'}
          sx={{ p: step.type === 'delay' ? 2 : 2 }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Avatar
                sx={{
                  backgroundColor: 'transparent',
                  width: step.type === 'delay' ? '32px' : '44px',
                  height: step.type === 'delay' ? '32px' : '33px',
                  mr: step.type === 'delay' ? 2 : '16px',
                  transition: 'transform 0.2s ease',
                  borderRadius: 0,
                }}
              >
                {step.type === 'delay' ? (
                  <AccessTimeIcon sx={{ color: '#ed5c2f', height: '32px', width: '32px' }} />
                ) : (
                  (() => {
                    const actionObj = COMPAIGNS_ACTION_TYPE.find((a) => a.type === step.action);
                    const Icon = actionObj?.icon;
                    return Icon ? (
                      <img
                        src={Icon}
                        alt={actionObj.label}
                        style={{ width: '100%', height: '33px' }}
                      />
                    ) : null;
                  })()
                )}
              </Avatar>
              <Box>
                <Typography
                  variant={step.type === 'delay' ? 'body1' : 'h6'}
                  className={step.type === 'delay' ? 'delayedCardTitle' : 'actionCardTitle'}
                  sx={{
                    mb: step.type === 'delay' ? 0 : 0.5,
                    mr: step.type === 'delay' ? 0 : 6,
                    fontFamily: 'Inter',
                    color: '#000',
                  }}
                >
                  {step.label}
                </Typography>
                {step.type === 'action' && (
                  <>
                    <Typography variant="body2" className="actionCardTemplateSubTitle">
                      {(() => {
                        const label = step?.template?.title
                          ? `ID:${step?.templateId} ${step.template.title}`
                          : step?.meta?.selectedTemplate?.label || '';

                        return label.length > 35 ? label.slice(0, 35) + '...' : label;
                      })()}{' '}
                    </Typography>
                    {step.config && Object.keys(step.config).length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        {Object.entries(step.config).map(([key, value]) => (
                          <Chip
                            key={key}
                            label={`${key}: ${value}`}
                            size="small"
                            sx={{
                              mr: 1,
                              backgroundColor: '#fff8ee',
                              color: '#ed5c2f',
                              border: '0.5px solid #ed5c2f',
                              fontFamily: 'Inter',
                              fontSize: '12px',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: '#ed5c2f',
                                color: '#fff',
                              },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </>
                )}
                {/* <Typography
                  variant="caption"
                  sx={{
                    color: '#999',
                    fontFamily: 'Inter',
                    mt: 1,
                    display: 'block',
                  }}
                >
                  Click to edit
                </Typography> */}
              </Box>
            </Box>
            {/* Actions Menu for Delay steps - Only show if not between actions */}
            {step.type === 'delay' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '15px',
                  right: 6,
                  display: 'flex',
                  zIndex: 1,
                  alignItems: 'center',
                }}
              >
                <IconButton
                  onClick={handleMenuClick}
                  disableRipple
                  sx={{
                    transition: 'all 0.2s ease',
                    opacity: anchorEl ? 1 : 0.4,
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      opacity: 1,
                    },
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <OlcActionMenu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
      >
        {/* Download Mailer Proof - Only for actions */}
        {/* {step.type === 'action' && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadMailerProof(
                step.meta.selectedTemplate.label,
                step.meta.selectedTemplate.id,
                step
              );
            }}
            disabled={downloadingProof.status && downloadingProof.stepId === step.id}
          >
            <ListItemIcon sx={{ minWidth: '20px !important' }}>
              {downloadingProof.status && downloadingProof.stepId === step.id ? (
                <CircularProgress size={14} sx={{ color: '#ed5c2f' }} />
              ) : (
                <img
                  src={DownloadCIcon}
                  alt="download proof"
                  style={{ width: '14px', height: '14px', opacity: 0.6 }}
                />
              )}
            </ListItemIcon>
            <ListItemText>Mailer Proof</ListItemText>
          </MenuItem>
        )} */}

        {/* Download Envelope Proof - Only for actions with envelope */}
        {step.type === 'action' && HAS_ENVELOPE.includes(step?.productId) && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadEnvelopeProof(step);
            }}
            disabled={downloadingEnvelope.status && downloadingEnvelope.stepId === step.id}
          >
            <ListItemIcon sx={{ minWidth: '20px !important' }}>
              {downloadingEnvelope.status && downloadingEnvelope.stepId === step.id ? (
                <CircularProgress size={20} sx={{ color: '#ed5c2f' }} />
              ) : (
                <MailIcon
                  fontSize="small"
                  sx={{ color: '#303030', stroke: '#ffffff', width: '14px', height: '14px' }}
                />
              )}
            </ListItemIcon>
            <ListItemText>Envelope Proof</ListItemText>
          </MenuItem>
        )}

        {/* Edit */}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleEditStep(step);
            handleMenuClose();
          }}
        >
          <img src={EditCampaignStepCIcon} alt="Edit" />
          <ListItemText>Edit</ListItemText>
        </MenuItem>

        {/* Delete - Only if not delay between actions */}
        {!isDelayBetweenActions && (
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              setOpenActionModal(true);
              handleMenuClose();
            }}
          >
            <img src={Delete2CIcon} alt="Delete" />
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </OlcActionMenu>

      {/* Add Button at the end (only for last step) */}
      {isLastStep && (
        <>
          {/* Line segment before add button */}
          <Box
            sx={{
              width: 2,
              height: '32px',
              backgroundColor: 'rgba(68, 66, 65, 0.3)',
              margin: '0 auto',
            }}
          />

          {/* Add Button */}
          <Tooltip title={`Add ${nextStepShouldBe === 'delay' ? 'Delay' : 'Action'}`}>
            <Fab
              size="small"
              sx={{
                backgroundColor: 'white',
                border: '1px solid rgba(68, 66, 65, 0.3)',
                color: '#303030',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                my: 1,
                '&:hover': {
                  borderColor: '#ed5c2f',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
                // '&:active': {
                //   transform: 'scale(0.95)',
                // },
                width: 42,
                height: 42,
                minHeight: 42,
              }}
              onClick={() => {
                const nextIndex = steps.length;
                if (nextStepShouldBe === 'delay') {
                  setOpenDelayDialog(nextIndex);
                } else {
                  setInsertIndex(nextIndex);
                  setOpenActionDialog(true);
                }
              }}
            >
              <img src={PlusStep} alt="Plus" style={{ width: '18px', height: '18px' }} />
              {/* <AddIcon fontSize="small" /> */}
            </Fab>
          </Tooltip>

          {/* Line segment after add button */}
          {/* {!isLastStep && (
            <Box
              sx={{
                width: 2,
                height: 15,
                backgroundColor: 'rgba(68, 66, 65, 0.3)',
                margin: '0 auto',
              }}
            />
          )} */}
        </>
      )}
      <ActionsModal
        openModal={openActionModal}
        handleCloseModal={() => setOpenActionModal(false)}
        onSubmit={() => {
          handleDeleteStep(index);
          setOpenActionModal(false);
        }}
        stepDelete={true}
      />
    </Box>
  );
};

export default StepCard;
